import { get, intersection, merge } from 'lodash';
import { interventions } from '~lib/common/config/interventions';
import { areAllScenesClinical } from '~lib/common/utils/scenes';
import makeMoulageLabel from '../../utils/makeMoulageLabel';
import isApplicableMoulage from '../../utils/isApplicableMoulage';

function getStateValue(stateId, caseConf) {
  if (!caseConf || !caseConf._initialStates) {
    return undefined;
  }
  return get(
    caseConf._initialStates.find(s => s.id === stateId),
    'options.value',
  );
}

function validateInterventions(caseConf) {
  const errors = [];
  const warnings = []
  const moulages = merge({}, caseConf.moulages, caseConf.structuralMoulages);
  const allScenesIndoor = areAllScenesClinical(caseConf);
  for (const caseIntervention of caseConf.availableInterventions || []) {
    const intervention = interventions.find(intv => intv.key === caseIntervention.key);
    if (!intervention) {
      continue;
    }
    // Applicability
    if (!isApplicableMoulage([intervention], moulages)) {
      errors.push({
        message: 'Invalid moulage and intervention combination.',
        category: 'patientConditions',
        field: 'moulage'
      });
      errors.push({
        message: 'Intervention enabled with no applicable moulage',
        category: 'interventions',
        field: 'intervention'
      });
      break; // only need one error
    }
    // Incompatiblity
    if (intervention.incompatibleMoulages) {
      const incompatible = intersection(intervention.incompatibleMoulages || [], Object.values(moulages));
      if (incompatible.length > 0) {
        const moulageLabel = makeMoulageLabel(incompatible[0])
        errors.push({
          message: `${moulageLabel} is incompatible with selected interventions (${intervention.label.en}).`,
          category: 'patientConditions',
          field: 'moulage'
        });
        break; // only need one error
      }
    }
  }
  for (const caseIntervention of caseConf.availableInterventions || []) {
    const intervention = interventions.find(intv => intv.key === caseIntervention.key);
    if (!intervention) {
      continue;
    }
    // Straps not allowed when every configured scene is an indoor clinical environment
    if (allScenesIndoor && intervention.treatment === 'STRAPS') {
      warnings.push({
        message: 'Straps not applicable for inpatient scenes.',
        category: 'interventions',
        field: 'intervention',
        metadata: {
          treatment: intervention.treatment,
        }
      });
      break; // only need one error
    }
  }

  // Find initial position and position changes in case logic
  const initPosition = getStateValue('position', caseConf);

  let foundPositionError = false;
  for (const availIntv of caseConf.availableInterventions || []) {
    const intv = interventions.find(intv => intv.key === availIntv.key);
    if (intv && intv.availableStances) {
      if (!intv.availableStances.includes(initPosition) && !foundPositionError) {
        errors.push({
          message: 'Position not compatible with selected interventions.',
          category: 'patientConditions',
          field: 'position',
          metadata: {
            treatment: intv.treatment,
          }
        });
        foundPositionError = true;
      }
    }
  }

  return {
    errors,
    warnings,
  };
}

export default validateInterventions;
